import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCache } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useAsyncEffect } from 'use-async-effect';
import { usePlayer } from '../../../PlayerProvider';

type SliderPosition = 'top' | 'bottom';

interface VolumeContextValue {
  sliderPosition: SliderPosition;
  setSliderPosition: (position: SliderPosition) => void;
  volume: number;
  setVolume: (volume: number) => void;
}
const VolumeContext = createContext<VolumeContextValue>({
  sliderPosition: 'top',
  setSliderPosition: () => {},
  volume: 0.5,
  setVolume: () => {}
});

export const useVolume = () => useContext(VolumeContext);

export function VolumeProvider({ children }: PropsWithChildren) {
  const [sliderPosition, setSliderPosition] = useState<SliderPosition>('top');
  const { volume, setVolume } = usePlayer();

  const [initialVolume, setInitialVolume] = useState<number | null>(null);
  const cache = useCache();
  const { volumeByDefault = 50, editMode } = useSettings();

  useAsyncEffect(
    async (isMounted) => {
      if (editMode) {
        const volume = parseInt(volumeByDefault.toString()) / 100;
        setInitialVolume(isNaN(volume) ? 0.5 : volume);
        return;
      }

      try {
        const savedVolumeLevel = (await cache.getCache(
          'volumeLevel',
          999999
        )) as number;
        if (isMounted()) {
          setInitialVolume(savedVolumeLevel);
        }
      } catch {
        if (isMounted()) {
          const volume = parseInt(volumeByDefault.toString()) / 100;
          setInitialVolume(isFinite(volume) ? volume : 0.5);
        }
      }
    },
    [volumeByDefault]
  );

  useEffect(() => {
    if (initialVolume !== null) {
      setVolume(initialVolume);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [initialVolume]);

  useEffect(() => {
    if (initialVolume !== null) {
      cache.setCache('volumeLevel', volume);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [volume]);

  const value = useMemo(
    () => ({
      sliderPosition,
      setSliderPosition,
      volume,
      setVolume
    }),
    [setVolume, sliderPosition, volume]
  );

  return (
    <VolumeContext.Provider value={value}>{children}</VolumeContext.Provider>
  );
}
